import { Link } from "@tanstack/react-router";
import { useRef, useState } from "react";

import ObscuredContent from "@/components/review-modal/review/obscured-content";
import { Button } from "@/components/ui/button";
import useIsTextExpandable from "@/hooks/use-is-text-expandable";
import { cn } from "@/lib/utils";

import type { UserReview, UserType } from "../../types";

interface ReviewSummaryProps {
  review: UserReview;
  user: UserType;
}

export default function ReviewSummary({ review, user }: ReviewSummaryProps) {
  const textRef = useRef<HTMLAnchorElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isObscured, setIsObscured] = useState(review.isContainingSpoilers);

  const isExpandable = useIsTextExpandable(textRef);

  const handleUnscramble = () => setIsObscured(false);

  const toggleMore = () => setIsExpanded((value) => !value);

  if ((review.summary || "").trim() === "") {
    return null;
  }

  if (isObscured) {
    return <ObscuredContent onClick={handleUnscramble} />;
  }

  return (
    <div className="relative rounded-[10px] border border-white/10 p-5 transition-colors hover:bg-white/5">
      <Link
        className={cn(
          "link-overlay whitespace-pre-line text-14/[1.7em] text-pale-lavender",
          !isExpanded && "line-clamp-5"
        )}
        ref={textRef}
        resetScroll={false}
        to={`/@${user.username}/reviews/review/${review.id}`}
      >
        {review.summary}
      </Link>
      {(isExpandable || isExpanded) && (
        <Button
          className="text-left text-14 text-white hover:underline"
          onClick={toggleMore}
        >
          {isExpanded ? "less" : "more"}
        </Button>
      )}
    </div>
  );
}
